import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", subject: "", message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Helmet>
        <title>Contact Vedanti Academy — Badlapur Branch</title>
        <meta name="description" content="Contact Vedanti Academy at +91 8308776505 or vedantiacademy@gmail.com. Visit our Badlapur branch near Badlapur Station. We serve students from Kalyan, Ambernath, Ulhasnagar, Dombivli & more." />
        <meta name="keywords" content="contact Vedanti Academy, coaching centre Badlapur, competitive exam classes near me, Vedanti Academy address" />
        <meta property="og:title" content="Contact Vedanti Academy | Badlapur" />
        <meta property="og:description" content="Reach out to us via phone, WhatsApp or visit our branch in Badlapur." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/contact" />
        <link rel="canonical" href="https://vedantiacademy.com/contact" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "Vedanti Academy",
          "url": "https://vedantiacademy.com",
          "telephone": "+918308776505",
          "email": "vedantiacademy@gmail.com",
          "address": {"@type": "PostalAddress", "streetAddress": "3rd Floor, Jadhav Market, above Aradhana Hotel", "addressLocality": "Badlapur", "addressRegion": "Maharashtra", "postalCode": "421503", "addressCountry": "IN"},
          "openingHours": "Mo-Sa 08:00-20:00",
          "aggregateRating": {"@type": "AggregateRating", "ratingValue": "5.0", "reviewCount": "500", "bestRating": "5"},
          "priceRange": "$$"
        })}</script>
      </Helmet>
      <section className="bg-vedanti-navy py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.15)_0%,_transparent_60%)]"></div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main relative z-10">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-white">Contact <span className="text-vedanti-orange">Us</span></h1>
          <p className="text-slate-300 mt-3 text-lg">Visit us or get in touch for any queries</p>
        </div>
      </section>

      <section className="section-padding bg-white" data-testid="contact-info-section">
        <div className="container-main max-w-3xl prose prose-slate">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-vedanti-navy">Get in Touch with Vedanti Academy, Badlapur</h2>
          <p>Vedanti Academy is Badlapur&rsquo;s trusted coaching institute for Banking, SSC, MBA CET, Railway and Insurance exam preparation. Whether you want to know about course fees, batch timings, upcoming exam notifications, study material or our &lsquo;Upto Selection Support&rsquo; policy, our team is always happy to help. We believe every aspirant deserves a quick, clear answer — so we offer multiple ways to reach us.</p>
          <h3 className="font-heading text-xl font-bold text-vedanti-navy">Visit Our Badlapur Branch</h3>
          <p>Our main branch is located on the 3rd Floor, Jadhav Market, above Aradhana Hotel, Badlapur, Maharashtra 421503 — just a two-minute walk from Badlapur Railway Station on the Central Line. Students from Ambernath, Ulhasnagar, Karjat, Neral, Vangni and Khopoli can reach us easily by train. Our AC classrooms, digital boards and in-house library are open Monday to Saturday, 8:00 AM to 8:00 PM. Walk in any time during working hours to meet our faculty and tour the campus.</p>
          <h3 className="font-heading text-xl font-bold text-vedanti-navy">Call, WhatsApp or Email</h3>
          <p>For the fastest response, call or WhatsApp us on +91&nbsp;8308776505 — our counsellors will answer your questions about courses, demo classes and admissions. You can also email us at vedantiacademy@gmail.com for detailed queries, fee structures or partnership enquiries, and we will get back to you within one working day.</p>
          <p>If you prefer, fill in the contact form on this page with your name, phone number and message, and our team will reach out to you personally. With 31+ achievers, 500+ 5-star Google reviews and a decade of teaching experience, Vedanti Academy is here to guide you at every step of your competitive-exam journey.</p>
        </div>
      </section>

      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Contact Info */}
            <div className="space-y-6">
              <div>
                <h2 className="font-heading text-2xl font-bold text-vedanti-navy mb-6">Get In Touch</h2>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-vedanti-navy rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-vedanti-navy mb-1">Badlapur Branch (Main)</h3>
                    <p className="text-slate-500 text-sm">3rd Floor, Jadhav Market, above Aradhana Hotel, Badlapur, Maharashtra 421503</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <a href="tel:+918308776505" className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-vedanti-orange/50 transition-colors" data-testid="contact-phone">
                  <Phone size={20} className="text-vedanti-orange" />
                  <div>
                    <p className="text-xs text-slate-400">Phone</p>
                    <p className="font-medium text-vedanti-navy">+91 8308776505</p>
                  </div>
                </a>
                <a href="mailto:vedantiacademy@gmail.com" className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 hover:border-vedanti-orange/50 transition-colors" data-testid="contact-email">
                  <Mail size={20} className="text-vedanti-orange" />
                  <div>
                    <p className="text-xs text-slate-400">Email</p>
                    <p className="font-medium text-vedanti-navy">vedantiacademy@gmail.com</p>
                  </div>
                </a>
                <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <Clock size={20} className="text-vedanti-orange" />
                  <div>
                    <p className="text-xs text-slate-400">Working Hours</p>
                    <p className="font-medium text-vedanti-navy">Mon-Sat: 8:00 AM - 8:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              {submitted ? (
                <div className="text-center py-16 bg-slate-50 rounded-2xl" data-testid="contact-success">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Send size={32} className="text-green-600" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-vedanti-navy mb-2">Message Sent!</h2>
                  <p className="text-slate-500">We&apos;ll get back to you as soon as possible.</p>
                </div>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
                  <h2 className="font-heading font-bold text-xl text-vedanti-navy mb-6">Send Us a Message</h2>
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Full Name *</Label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" required data-testid="contact-name-input" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Phone *</Label>
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXXXXXXX" required data-testid="contact-phone-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Subject</Label>
                      <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="How can we help?" data-testid="contact-subject-input" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Message *</Label>
                      <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Write your message here..." rows={5} required data-testid="contact-message-input" />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="bg-vedanti-navy text-white hover:bg-vedanti-navy/90 rounded-lg px-8 py-3.5 font-bold transition-all disabled:opacity-60"
                      data-testid="contact-submit-btn"
                    >
                      {loading ? "Sending..." : "Send Message"}
                    </button>
                  </form>
                </div>
              )}

              {/* Map */}
              <div className="mt-8 rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3770.7!2d73.2287!3d19.1688!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7f3be1abc1111%3A0x1111111111111111!2sBadlapur%2C%20Maharashtra%20421503!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  title="Vedanti Academy Badlapur Location"
                  data-testid="contact-map"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
